// In-browser Pi agent wired to the tenant's Origin-backed LLM config
// and the analytics graph API.
//
// The agent loop (state, tool routing, streaming) runs entirely in the
// browser via @earendil-works/pi-agent-core. LLM completions used to
// go straight from the browser to the upstream provider with the
// user's own API key in localStorage — that's now routed through the
// backend tenant LLM proxy (Origin O-690 / O-892 / O-893):
//
//   pi-ai SDK → fetch(api.anthropic.com/...) → [Service Worker
//   intercepts requests destined for known LLM provider hostnames]
//   → POST ${ORIGIN}/v1/tenant/llm-proxy with `Authorization: Bearer
//   <originJWT>` → backend proxy strips the JWT, injects the stored
//   provider key, forwards to the upstream.
//
// What that buys us: provider API keys are stored server-side at the
// tenant level; the browser only ever sees `has_api_key: bool` plus
// the Origin JWT it already has. pi-ai's per-provider request shaping,
// streaming, tool calls, cache headers, etc. stay completely
// untouched — the SW only rewrites URL + auth header.

import { Agent } from "@earendil-works/pi-agent-core";
import { installDefaultStreamFn } from "./piStreamFn.js";
import { setSystemPrompt } from "./agentTranscript.js";
import { defaultTaskModelFor, lookupCatalogModel } from "./modelCatalog.js";

import { getChatCreds } from "./chatCreds.js";
import { setCustomLlmHost } from "./llmProxyShim.js";
import { encodeChatCredential } from "./chatCredential.js";
import { describeSchema } from "./analyticsClient.js";
import { createAnalyticsQueryTool, createExpandPreviewTool, analyticsSchemaTool } from "./analyticsTool.js";
import { withQueryGuidance } from "./queryErrorGuidance.js";
import { schemaTools } from "./schemaSearch.js";
import { buildSchemaIndexPrompt } from "./schemaPromptIndex.js";
import { matchProxyUpstreamApi, stripProxyUnsupportedCompat } from "./proxyModelCompat.js";
import { harnessMemory } from "./harnessMemoryStore.js";
import { createRunLedger, withHarnessMemory, distillRun, replaceLessonsBlock } from "./harnessMemoryRun.js";
import { createRememberTool } from "./harnessMemoryTool.js";
import { attachLiveGraph, createGraphStore, createGraphTools, createPivotTool, createExpandTools, feedGraph, deltaNote, replaceGraphBlock, withGraphHints, graphSnapshot, GRAPH_PROMPT_NOTE, GRAPH_PROMPT_BUDGET } from "./investigationGraphRun.js";
import { renderGraph } from "./investigationGraph.js";
import { countRunToolCalls, createRecipeTools, describeRecipe, describeRecipeWithModel, dropProposal, lastUserText, proposalOutcome, recipeProposalText, recipesPromptSection, replaceRecipesBlock, settleRecipeRun, withTrace } from "./graphRecipes.js";
import { createRecipeStore } from "./recipeStore.js";
import { syncOrgRecipes } from "./orgRecipes.js";
import { createSpawnSubAgentTool, createSpawnBatchTool } from "./subAgentTools.js";
import { createSpawnQueryTool } from "./spawnQueryTool.js";
import { createPythonTool } from "./pythonTool.js";
import { createRenderBlockTool } from "./kernelRenderTool.js";
import { createSuggestKernelTool } from "./kernelSuggestTool.js";
import { createSetStarterCardsTool } from "./starterCardsTool.js";
import { setTailoredCanvasesTool } from "./tailoredCanvasesTool.js";
import { createCanvasTools } from "./canvasTools.js";
import { createNavigationTools } from "./navigationTools.js";
import { AUDIT_TOOLS } from "./auditTools.js";
import { createMessageClassifierTools } from "./messageClassifierTools.js";
import { createChildHandleRegistry } from "./childHandleRegistry.js";
import { instrumentSteer } from "./steerEvents.js";
import { stampCurrentDate } from "./promptDate.js";
import { createSupervisorTools } from "./supervisorTools.js";
import { evictMissingFromMessages, evictLargeBeyondBudget, getResult as getStoredResult } from "./toolResultStore.js";
import { createRehydrator } from "./toolResultRehydrate.js";
import { getAccessToken } from "./authBridge.js";
import { makeAfterToolCall } from "./chatAfterToolCall.js";
import { compactMessages, compactionOptionsFor, estimateTokens, CHARS_PER_TOKEN, TOKEN_BUDGET } from "./contextCompaction.js";
import {
    PYTHON_EXECUTION_SYSTEM_NOTE,
    PYTHON_FRESH_GLOBALS_HYGIENE_NOTE,
} from "./pythonExecutionSystemPrompt.js";

// Synthesize a Model when pi-ai's catalog doesn't know the id the
// admin saved on the backend. Catalog hits route through the
// provider's native API (Anthropic Messages, OpenAI Responses, etc.);
// misses fall through to openai-completions with a provider-shaped
// baseUrl. Cost/context fields are unused by the agent loop itself;
// conservative defaults are fine.

function synthOpenAiModel({ modelId, provider, baseUrl }) {
    return {
        id: modelId,
        name: modelId,
        api: "openai-completions",
        provider,
        // `custom` passes its own gateway base URL; the hardcoded providers
        // fall back to their canonical URL (the SW rewrites the host to the
        // backend proxy regardless — the URL just has to look provider-shaped).
        baseUrl: baseUrl || providerBaseUrl(provider),
        reasoning: false,
        input: ["text"],
        cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
        contextWindow: 128000,
        maxTokens: 4096,
    };
}

// Backend's closed enum of providers. In proxy mode the SW
// intercepts requests to these hosts and rewrites them to the
// backend proxy URL — but pi-ai/openai-completions still expects
// the URL to look provider-shaped, e.g. for the cache-header /
// OpenRouter routing auto-detection (`baseUrl.includes("openrouter.ai")`).
function providerBaseUrl(provider) {
    switch (provider) {
        case "anthropic":  return "https://api.anthropic.com";
        case "openrouter": return "https://openrouter.ai/api/v1";
        case "openai":     return "https://api.openai.com/v1";
        default:           return "https://api.openai.com/v1";
    }
}

// `custom` always synthesizes the openai-completions Model against the
// tenant's own base URL — pi-ai has no catalog for it. Other providers try
// the catalog first, falling back to the synth for ids it doesn't list.
function buildModel({ model: modelId, provider, baseUrl }) {
    const catalog = provider && provider !== "custom" ? lookupCatalogModel(provider, modelId) : null;
    // Requests travel through the tenant LLM proxy, which forwards no
    // anthropic-beta header and picks the upstream endpoint from the
    // tenant's provider alone; see proxyModelCompat.js.
    return catalog
        ? stripProxyUnsupportedCompat(matchProxyUpstreamApi(catalog))
        : synthOpenAiModel({ modelId, provider, baseUrl });
}

// Pre-fetch the live analytics schema so it can be inlined into the
// system prompt. Smaller / older models do much better when entity
// names, field enums, edge names, and the schema's own example
// queries are visible in context — they otherwise tend to invent
// plausible-but-wrong enum spellings on the first call. `describeSchema`
// is memoized in analyticsClient, so subsequent agent creations within
// the same browser session reuse the cached payload.
async function loadAnalyticsSchema() {
    try {
        return await describeSchema();
    } catch (err) {
        console.warn("[chat] failed to fetch analytics schema for system prompt:", err);
        return null;
    }
}

// Built-in tool groups a surface can leave out. Each goes with the prompt
// sections that teach it, so a surface that drops a group is never told to
// call a tool it does not have.
//   display     - render_block, suggest_kernel, the canvas, starter cards and
//                 tailored canvases: results handed to UI the chat draws
//   navigation  - open_in_explore, open_studio_item: moving the page
//   audit       - list_chats, read_chat_transcript, read_tool_call: reading
//                 the other chats open in this window
//   supervision - steering and collecting sub-agents a user message cut short
//   mcp         - the tools of the tenant's connected MCP servers
//   classifiers - training and saving the tenant's message classifiers for
//                 the findings verifier; a tenant admin's tools only
const TOOL_GROUPS = ["display", "navigation", "audit", "supervision", "mcp", "classifiers"];

function toolGroupSet(groups) {
    const set = new Set(groups || []);
    for (const group of set) {
        if (!TOOL_GROUPS.includes(group)) throw new Error(`Unknown tool group "${group}"; expected one of ${TOOL_GROUPS.join(", ")}`);
    }
    return set;
}

export function buildSystemPrompt(schema, omitToolGroups = []) {
    const omitted = toolGroupSet(omitToolGroups);
    const has = (group) => !omitted.has(group);
    const intro = has("display")
        ? "You answer questions about analytics data by calling the `analytics_query` tool, then surface results visually with `render_block` when they're naturally time-series, tabular, or comparative."
        : "You answer questions about analytics data by calling the `analytics_query` tool.";
    const subagentNote = [
        "You can delegate focused subtasks to sub-agents:",
        "  • `spawn_query` — narrow specialist. Hand it a query you can't get right; it returns a CANDIDATE `{ spec, notes }` — NOT a guarantee it runs. The specialist may hand back a spec it never executed, so always run it yourself, and if it fails, debug it as your own — a returned spec is not proof the shape works. Reach for this BEFORE retrying analytics_query a third time on the same shape — it's cheaper than burning more of your own turns on schema lookups, and the resulting rows land in your own context (not the specialist's). Don't use it for trivial typos you can fix from the error message; use it when you genuinely don't know which field/edge/operator to use.",
        "  • `spawn_subagent` — open-ended task with its own context. Useful for long-form work, rule evaluation, or anything that doesn't need this chat's history.",
        "  • `spawn_batch` — fan the same task out across a list of items in parallel (concurrency-limited). Use whenever you'd otherwise loop sequentially through 5+ items.",
        "",
        "Pick the tools each child needs (subset of your own — schema_search / schema_describe_entity / analytics_query / execute_python are common picks). Children DO NOT have the analytics schema in their system prompt — they use `schema_list_entities`, `schema_describe_entity`, and `schema_search` to look up entities and fields on demand. Always pass at least `schema_search` if the child will issue analytics queries.",
        "",
        "For structured output, pass `output_schema` describing the shape you want; the child gets a `return_result` tool it must call once with a conforming object.",
    ].join("\n");
    const supervisorNote = [
        "Sub-agent supervision (handles steering during a spawn):",
        "  • Normally spawn_subagent and spawn_batch BLOCK until the child(ren) finish — you await the result inline.",
        "  • If the user sends a message while one of those tools is waiting on a child, the spawn returns EARLY with a handle id and the child keeps running in the background. You'll see the user's new message in the same turn alongside the interrupted spawn result.",
        "  • When that happens, decide whether the user's redirection changes the child's relevance:",
        "      — `steer_subagent({ child_id, message })` — inject new context into a running child (broadcasts to all items for a batch handle)",
        "      — `abort_subagent({ child_id })` — discard a child whose work is no longer relevant",
        "      — `await_subagent({ child_id })` — wait for the child to finish and collect its result",
        "      — `list_subagents()` — see every live handle",
        "  • If the user's redirection doesn't affect the running child, just respond normally — the child finishes on its own and is cleaned up automatically.",
        "  • These supervisor tools only do anything when there's actually a live handle. Don't call them speculatively.",
    ].join("\n");
    const pythonNote = PYTHON_EXECUTION_SYSTEM_NOTE;
    const renderNote =
        "When you have a final result that's naturally visual or tabular, render it with `render_block` instead of pasting raw JSON. Kinds: `chart` (time-series, line or stacked, with hover tooltip), `sparkline` (compact inline indicator), `stat` (big number callout), `bars` (horizontal share-of-max for categorical comparison), `table` (tabular), `rows` (compact top-N list), `markdown` (a templated prose/narrative readout when the answer is text rather than a chart), plus density / composition / flow kinds. Same render specs power saved kernels later — anything you render this way is one step from becoming a reusable dashboard widget.";
    const canvasNote = [
        "Canvas — a persistent surface alongside the chat that holds renders the user wants kept in view across turns. Tools: `canvas_inspect`, `canvas_add`, `canvas_update`, `canvas_remove`, `canvas_clear`.",
        "",
        "Pin to the canvas when:",
        "  • the user explicitly asks (\"add the heatmap to the canvas\", \"pin this\", \"keep that on the side\");",
        "  • you're building an evolving multi-view summary across turns and earlier views remain useful as the user iterates (e.g. they're comparing models and want each chart to stay visible while you add the next).",
        "Do NOT auto-pin every render_block. Most renders are transient inline answers that belong only in the chat trail.",
        "",
        "Workflow when modifying the canvas:",
        "  1. `canvas_inspect()` to see what's already pinned (ids, kinds, captions, provenance status).",
        "  2. If a similar item exists, `canvas_update(id, …)` rather than adding a duplicate.",
        "  3. Otherwise `canvas_add(spec, data, caption, sourceToolCallIds)`. Write a clear short caption — it's the user-facing label on the canvas card.",
        "  4. `canvas_remove(id)` when the user asks to drop one; `canvas_clear()` only if they explicitly want a wipe.",
        "",
        "PROVENANCE — `sourceToolCallIds` (load-bearing for the kernel-save path):",
        "Whenever you call `canvas_add` (or refresh an item via `canvas_update`), ALSO pass `sourceToolCallIds`: the list of `_tool_call_id`s of the analytics_query / execute_python tool calls whose output computed this item's `data`. The id is the `_tool_call_id` field echoed at the top of each prior tool result — copy verbatim. Include only the SUCCESSFUL tool calls in the chain (skip failed retries).",
        "Examples:",
        "  • One analytics_query (id=toolu_q_xyz) feeds straight into a render_block → `sourceToolCallIds: [\"toolu_q_xyz\"]`.",
        "  • analytics_query (id=q1) → execute_python (id=py1) that consumed q1 via from_tool_calls → `sourceToolCallIds: [\"q1\", \"py1\"]`.",
        "Why this matters: the user can later trigger a \"crystallise canvas into saved kernels\" action that emits one persistent kernel per canvas item. With provenance, that path is deterministic; without it, a sub-agent has to scan the whole transcript heuristically and is far more failure-prone.",
        "",
        "Canvas items carry frozen data snapshots — they do NOT live-recompute. To refresh data, call `canvas_update` with new `data` (and ideally new `sourceToolCallIds` pointing at the fresh tool calls).",
    ].join("\n");
    const navNote = [
        "Navigate the page for the user. The dashboard's MAIN column shows the live, drillable data graph; the chat often sits docked beside it. Two tools move that main column:",
        "  • `open_in_explore({ scopePath, requested? })` — open a data-graph scope as a tab. `scopePath` is ordered steps `{ dim, ids }`; later steps nest within earlier ones. The `ids` MUST come from an analytics_query you actually ran (group by that dim, copy the group-key values) — never invent ids. Navigable dims: endpoints, agents, sessions, clusters, models, tools, skills, mcp_servers. The tool verifies the scope has data IN THE CURRENTLY SELECTED TIME RANGE and rejects it otherwise — if you get that error, either the ids are stale/invented (run the query first) or there's genuinely no activity in this window (tell the user to widen the range).",
        "  • `open_studio_item({ view, itemId, requested? })` — open a saved kernel / canvas / workflow in Studio. `view` is kernels|canvases|workflows; `itemId` is the command / slug / command. The tool rejects an item that isn't saved — don't guess names.",
        "",
        "When to navigate vs render inline: a `render_block` answers a question in place; navigation hands the user a live surface to keep exploring. Reach for navigation when the user asks to \"show me / take me to / pull up / open\" something, or when the natural next step is for them to drill around the data themselves.",
        "Set `requested: true` ONLY when the user explicitly asked to be taken somewhere — then a docked chat opens the tab immediately. When you're volunteering (or the chat is a floating overlay), leave it false and the user gets a one-click chip instead of having the page move under them. Opening a scope/item that's already a tab just activates it, so it's safe to offer the same destination again.",
        "Write session ids in full.",
    ].join("\n");
    const suggestNote = [
        "After ANY general-purpose analysis — one where parameter values (thresholds, time windows, top-N counts) could plausibly vary and the user might rerun it later against fresh data — proactively call `suggest_kernel`. That surfaces a one-click Save card; accepting runs the existing kernel-save flow (validation + persistence). This is the default: don't wait to be asked.",
        "Crucially, a kernel is NOT gated on having drawn a chart. Pick the pattern by what you produced:",
        "  • Pattern A — you ended this turn with a successful `render_block`. Call suggest_kernel with just `command` + `title`; the save flow lifts the render spec from the transcript.",
        "  • Pattern B — you answered in PROSE / a text table, or the chart-worthy result was from an earlier turn (so there's no render_block in THIS turn). Still suggest — pass a `proposedRender` spec describing how the saved kernel should display. If the answer was genuinely a written readout rather than a chart, use a `markdown` render (a template over the row data: `{{#each rows}}`, `{{#if}}`, `{{ field | format }}`) so the prose you'd have typed becomes a re-runnable widget. This is the path that makes \"the model wrote it out instead of charting it\" still kernel-able.",
        "Don't suggest for one-off questions, dead-end exploration, or analyses whose value was a free-form judgement with no underlying queryable data shape (no analytics_query / execute_python to rebuild from).",
        "If your answer was a multi-view CANVAS rather than a single render_block, do NOT call suggest_kernel — the canvas crystallises into kernels via its own path, and Pattern A (no `proposedRender`) will just error because there's no single prior render_block to lift. Either render a single block and suggest that, or skip the suggestion for canvas-only answers.",
    ].join("\n");
    const queryHygieneNote = [
        "Query hygiene — traps that cause repeated failures:",
        "",
        "  • Some dims are SYNTHETIC, not entities. `skills` and `mcp_servers` are navigable in `open_in_explore`, but the analytics graph has NO `skill` / `mcp_server` entity or field — `schema_search` will (correctly) find nothing. They're derived from Array(String) projections on the PROMPT entity, queryable element-wise with LIKE / ILIKE: skills live in `DIMENSION_FIELD_PROMPT_RESOURCE_PATHS` as paths shaped `…/skills/<name>/…` (the skill id is `<name>`); MCP servers live in `DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES` as `mcp__<server>__<tool>` (the server id is `<server>`). To analyze either, group/filter on that field and slice the name out yourself (in `execute_python` if needed) — do NOT substitute raw tool names for skills, and do NOT tell the user the data doesn't exist. Note: there is no version / fingerprint / hash field for skills in the graph — that lives in the Skills management surface, not here.",
        "",
        "  - Use normalized conversation search and typed process, file, DNS, and network fields. Do not select or search EVENT_EVENT_DATA, BODY_JSON, or whole-object inventory fields. Discover supported scalar fields with schema_search. If the needed fact is not exposed, report that limitation instead of scanning serialized payloads.",
        "",
        "  - Attribution (who ran X, on which machine, in which session) rests on EXECUTION records, never on prompt summaries. Prompt summaries and CONVERSATION_TOOL_CALL names say what an agent intended or how often it called a tool; a PROCESS_EVENT command line says what ran, when, on which endpoint, and carries the TRACE_ID of the session that ran it. Search the action fleet-wide first (PROCESS_EVENT_COMMAND_LINE ILIKE '%term%', or TELEMETRY_TIMELINE_SEARCH_TEXT ILIKE 'term', over the time window), then narrow to the endpoint and trace the hit names, then confirm the model's own call with CONVERSATION_SEARCH_CONTENT scoped to that TRACE_ID (part_kind tool_use). For Claude Code sessions TRACE_TOOL_ACTIVITY_TOOL_INPUT is also an execution record; OpenAI (Codex, ChatGPT) and Cursor sessions report only session metadata there, so their tool calls live in the conversation content.",
        "",
        "  - Window bounds per entity family: OS telemetry (PROCESS_EVENT, TELEMETRY_TIMELINE, DNS_EVENT, FILE_EVENT, NETWORK_CONNECTION) and text search views accept at most 30 days (2592000s); trace-detail and prompt entities up to 90 days. A fleet-wide command-line or search-text scan over the full 30 days reads past the 8 GiB execution ceiling; start with the narrowest window around the moment you care about (minutes, not days) and widen only if it is empty, or add an endpoint or session equality filter first.",
        "",
        "  - Conversation entities (CONVERSATION_TRACE, CONVERSATION_MESSAGE, CONVERSATION_TOOL_CALL, CONVERSATION_SEARCH_CONTENT) are windowed by the TRACE'S START TIME, not by when a message happened. A window around the moment of an action misses every message of a session that started earlier. Scope them by TRACE_ID with a window that contains the session's start, and read CONVERSATION_MESSAGE_MESSAGE_AT for when a message actually happened. Fleet-wide CONVERSATION_SEARCH_CONTENT text search scans the whole table and hits the execution limit; trace-scoped it is cheap.",
        "",
        "  • Absolute windows are `timeRange: { absolute: { start: \"2026-09-09T23:15:00Z\", end: \"2026-09-09T23:45:00Z\" } }` — RFC 3339 UTC, keys exactly `start` and `end`. Relative windows are `{ relative: { durationSeconds: \"604800\" } }`. Predicate values are plain JSON (`\"value\": [\"a\", \"b\"]` for IN, never protobuf `listValue` / `stringValue` wrappers).",
        "",
        "  • Results cap at ~2000 rows. A capped raw-row pull silently truncates and skews any client-side rollup — when you need counts or shares, compute them server-side with group by + aggregates and pull only the small grouped result, never the underlying rows.",
        "",
        "  • Relative time ranges cap at ~90 days. A `RelativeTimeRange.duration_seconds` over 7776000 (~90d) is rejected (`400`, \"exceeds the maximum allowed\"). Stay within 90 days (or use an explicit absolute range inside retention) — don't keep re-sending the same over-long window.",
        "",
        "  • Predicate literals are type-checked. A Bool field (e.g. `DIMENSION_FIELD_ILO_EVENT_CLUSTER_IS_OUTLIER`) needs a real boolean, not the string \"false\" (that 400s with \"expects Bool\"). `IN` / `NOT_IN` need a non-empty list value; `op` must be a real operator (there is no `NEQ` — use `NE`). When a 400 complains about a value / type / operator, fix that literal — don't swap entities or abandon the field.",
        "",
        "  • Don't loop. If a query shape already failed, do NOT re-send it unchanged — change the window, operator, or value, or step back and rethink. " +
            PYTHON_FRESH_GLOBALS_HYGIENE_NOTE +
            " If you catch yourself repeating a step or narrating \"I'm going in circles,\" STOP: take a genuinely different approach or tell the user what's blocking you. Many identical calls are worse than one honest \"here's where I'm stuck.\"",
        "",
        "  • ILO clusters have a specific shape — get it right or you'll thrash. Human-readable THEMES are the `ILO_CLUSTER` rows at `DIMENSION_FIELD_ILO_CLUSTER_HIERARCHY_LEVEL = 0` (bisecting generations put LLM-named top-level themes at level 0; deeper levels can still carry machine keyword labels). `ILO_CLUSTER` has NO time field — never time-window it, and `POINT_COUNT` is cumulative all-time, not recent activity. Prompts/events attach only to LEAF clusters via `ILO_EVENT_CLUSTER`; to measure a theme's recent activity, filter `ILO_EVENT_CLUSTER` on the virtual `DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE = <level-0 id>` (or `IN (...)`; EQ / IN only, filter-only — not selectable / groupable) and the planner rolls every descendant leaf up server-side. Both `ILO_CLUSTER` and `ILO_EVENT_CLUSTER` AUTO-PIN to the latest completed generation and their id spaces already align — do NOT query `ILO_CLUSTER_GENERATION` or pin a generation manually unless you specifically want an older one. Shape for 'themes over time': list level-0 themes → for the top few, count `ILO_EVENT_CLUSTER` by week with `cluster_subtree = <theme id>` → render. That's a handful of queries, not dozens.",
    ].join("\n");
    if (!schema) {
        return [
            intro,
            "",
            subagentNote,
            "",
            ...(has("supervision") ? [supervisorNote, ""] : []),
            pythonNote,
            "",
            ...(has("display") ? [renderNote, "", canvasNote, ""] : []),
            ...(has("navigation") ? [navNote, ""] : []),
            ...(has("display") ? [suggestNote, ""] : []),
            queryHygieneNote,
            "",
            "Call `analytics_describe_schema` first to discover the available entities, fields, and edges before composing a query.",
        ].join("\n");
    }
    return [
        intro,
        "",
        subagentNote,
        "",
        GRAPH_PROMPT_NOTE,
        "",
        ...(has("supervision") ? [supervisorNote, ""] : []),
        pythonNote,
        "",
        ...(has("display") ? [renderNote, "", canvasNote, ""] : []),
        ...(has("navigation") ? [navNote, ""] : []),
        ...(has("display") ? [suggestNote, ""] : []),
        queryHygieneNote,
        "",
        // An index of the graph replaces the full DescribeSchema dump
        // (~110k tokens on a real tenant): entity descriptions, core
        // field names, every edge, and a few complete specs. Field lists
        // for the rest come from schema_describe_entity on demand, the
        // same way sub-agents have always worked.
        buildSchemaIndexPrompt(schema),
    ].join("\n");
}

// The analytics chat's built-in tools in the order the model sees them, less
// any group the surface leaves out. The spawn tools resolve their children's
// tools from this list, so a child cannot be handed a group its parent lacks.
export function assembleChatTools(tools, omitToolGroups = []) {
    const omitted = toolGroupSet(omitToolGroups);
    const group = (name) => (omitted.has(name) ? [] : tools[name] || []);
    return [
        ...tools.analytics,
        ...tools.memory,
        ...group("audit"),
        ...group("display"),
        ...group("navigation"),
        ...tools.schema,
        ...tools.python,
        ...group("classifiers"),
        ...group("mcp"),
        ...tools.spawn,
        ...group("supervision"),
    ];
}

// Convert Pi's extended AgentMessage[] (which may include our custom
// `notification` role) into LLM-compatible messages. Notifications
// carry transient dashboard context — the page/scope/range the user
// is currently viewing — that the agent should treat as background
// info, not as something the user explicitly typed. We wrap them in
// a `<dashboard_context>` block so the model can tell them apart
// from real prompts.
export function convertToLlm(messages) {
    return messages.flatMap((m) => {
        if (m.role === "notification") {
            return [
                {
                    role: "user",
                    content: `<dashboard_context>${m.text}</dashboard_context>`,
                    timestamp: m.timestamp,
                },
            ];
        }
        return [m];
    });
}

// Compaction lives in contextCompaction.js (pure, unit-tested). Re-exported
// here because ChatConsole's /context and /compact and the audit tools import
// it from this module.
export { compactMessages, compactionOptionsFor, estimateTokens };

// ── Model-relative tool-result caps ────────────────────────────────
// The effective ceiling on what a tool result can ever deliver to the
// model is the smaller of the model's context window and TOKEN_BUDGET
// (past either, compaction stubs it or the provider rejects the
// request). Caps derived from this ceiling never clip anything the
// model could genuinely read — they only convert an inevitable silent
// loss into an immediate, actionable truncation notice.
const FALLBACK_CONTEXT_TOKENS = 128_000;

export function contextCeilingBytes(contextWindowTokens) {
    const tokens = Math.min(Number(contextWindowTokens) || FALLBACK_CONTEXT_TOKENS, TOKEN_BUDGET);
    return tokens * CHARS_PER_TOKEN;
}

// A single inlined result may fill MOST of the window — the rest of
// the conversation still needs room.
export const INLINE_RESULT_WINDOW_FRACTION = 0.7;
// The generic content clamp sits strictly ABOVE the ceiling: anything
// it touches could never be fully read even in an empty conversation.
export const CONTENT_CLAMP_WINDOW_FRACTION = 1.1;

// Closure-style transformContext per-chat: compaction against the live
// model's budget, then eviction passes against the chat's tool result
// store. Eviction keys off the agent's RAW history, not the compacted
// projection: the digest names tool_call_ids so the model can still reach
// rows whose inline result was folded, and only a fork / rewind / manual
// /compact that really drops a message should drop its rows.
//
// Wrapping is per-chat because both eviction passes are
// namespace-scoped, and the namespace is the chatId — which is only
// known after createChatAgent receives its options.
function makeTransformContext({ getNamespaceKey, getAgent }) {
    return async (messages) => {
        const compacted = compactMessages(messages, compactionOptionsFor(getAgent?.()));
        const namespaceKey = getNamespaceKey?.() || "default";
        evictMissingFromMessages(namespaceKey, messages);
        // Soft per-chat byte budget for LARGE entries. Oldest large rows
        // get their payload dropped (a marker stays so rehydration can
        // detect "used to exist"). Idempotent under-budget.
        evictLargeBeyondBudget(namespaceKey);
        return compacted;
    };
}

// Shared runtime that any agent in this app (main chat or any
// sub-agent the chat spawns via spawn_subagent / spawn_batch) needs
// to talk to the configured LLM. Pulls creds, validates them, ensures
// the SW proxy is live, and returns the building blocks rather than a
// finished Agent so callers can add their own tools / system prompt /
// context transform.
//
// `role`: "primary" uses the user's chosen model; "task" uses the
// optional taskModel for sub-agent work (kernel save, judges), with
// auto-default per provider and silent fallback to primary when no
// task model is configured.
// pi-agent-core 0.83.0 requires the host to name the stream function and
// throws when an Agent is constructed without one. Every Agent in this app is
// built through buildAgentRuntime below or through subAgent, which imports it
// from here — so installing at this module's load covers both.
installDefaultStreamFn();

export async function buildAgentRuntime({ role = "primary", modelId: modelIdOverride = null, serverChatId = null } = {}) {
    const creds = await getChatCreds();
    if (!creds.tenantId) {
        throw new Error("No active tenant — sign in and try again.");
    }
    if (!creds.provider || !creds.model) {
        throw new Error("Chat LLM not configured — ask an admin to set provider and model.");
    }
    // A tenant with no key but served on Origin's managed key
    // (`managedDefault`) is usable — only require a key when managed mode is
    // not active for this tenant.
    if (!creds.hasApiKey && !creds.managedDefault) {
        throw new Error("Chat LLM key not yet configured — ask an admin to save an API key.");
    }

    // For a custom (BYO-gateway) provider, register its host with the proxy
    // shim so the SDK's outbound fetch to the custom base URL is routed
    // through the backend proxy (keeping the provider key server-side),
    // exactly like the hardcoded provider hosts. Clear it otherwise so a
    // tenant that switched away from custom stops intercepting that host.
    if (creds.provider === "custom" && creds.baseUrl) {
        try {
            setCustomLlmHost(new URL(creds.baseUrl).host);
        } catch {
            setCustomLlmHost(null);
        }
    } else {
        setCustomLlmHost(null);
    }

    // Hand pi-ai a credential the in-process fetch wrapper (`llmProxyShim`)
    // intercepts: it redirects the SDK's outbound LLM request to the backend
    // proxy and normalizes both Authorization/x-api-key forms. When a server
    // chat id is in scope the credential is a composite carrying the chat id +
    // role (see chatCredential.js); the shim splits it back into the clean
    // Origin JWT bearer plus `X-Origin-Chat-Id` / `X-Origin-Chat-Role` audit
    // headers. Concurrency-safe by construction — the context rides the
    // returned value, so sub-agents and concurrent chats never share mutable
    // state. Without a chat id (any focused/non-persisted surface) this is the
    // bare JWT, exactly as before. The upstream provider key is injected
    // server-side.
    const getApiKey = () => encodeChatCredential({
        chatId: serverChatId,
        role,
        jwt: getAccessToken() || "no-jwt",
    });

    // An explicit override (e.g. a surface-local model picker bumping a
    // one-shot job to a bigger context window) wins over the role
    // defaults. Same provider + creds either way — the proxy injects
    // the provider key server-side regardless of model.
    const modelId = modelIdOverride
        || (role === "task"
            ? (creds.taskModel || defaultTaskModelFor(creds.provider) || creds.model)
            : creds.model);

    return {
        creds,
        role,
        modelId,
        model: buildModel({ ...creds, model: modelId }),
        convertToLlm,
        getApiKey,
    };
}

// Build an agent for the main analytics chat (default) OR for a
// focused sub-surface (kernel editor, etc.) by passing custom
// options:
//   - extraTools: append additional tools to the built-in family
//   - extraToolsWith: ({ subscribeToParentSteer, childRegistry, getAgent })
//     -> tools, appended after extraTools: tools that delegate work the way
//     spawn_batch does (yield to a user steer, leave the work running in the
//     chat's child registry) get the same wiring the spawn tools get
//   - toolsWhile: [{ tools, when }] tools a run carries only while `when()`
//     holds as it starts (a surface's tools, offered while it is up)
//   - replaceToolsWith: REPLACE the built-in family entirely with
//     this list (still includes MCP tools for compatibility).
//     Use when the surface should NOT have access to canvas /
//     suggest_kernel / audit / spawn etc.
//   - systemPrompt: skip the analytics system prompt entirely and
//     use this verbatim. For focused agents that don't want the
//     full analytics guidance.
//   - systemPromptSuffix: append to the analytics system prompt.
//     Mutually exclusive with `systemPrompt`.
// The model's last words in the transcript: what it said when it declined a
// proposed recipe.
function lastAssistantText(messages) {
    for (let i = (messages || []).length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m?.role !== "assistant" || !Array.isArray(m.content)) continue;
        const text = m.content.filter((b) => b?.type === "text" && typeof b.text === "string").map((b) => b.text).join("\n").trim();
        if (text) return text;
    }
    return "";
}

// Tools a run carries only while their condition holds as it starts: each
// run takes its own copy of the agent's tools, so a surface's tools reach the
// model while the surface is up and not otherwise. A group's `tools` may be
// made as the run starts (a function, asked after `when`). A name the run
// already carries keeps its first tool: the agent's own, then the groups' in
// order.
export function offerToolsWhile(agent, base, groups) {
    const set = () => {
        const extra = groups.flatMap((g) => (g.when() ? (typeof g.tools === "function" ? g.tools() || [] : g.tools) : []));
        const named = new Set(base.map((t) => t.name));
        const fresh = extra.filter((t) => t?.name && !named.has(t.name) && named.add(t.name));
        agent.state.tools = fresh.length ? [...base, ...fresh] : base;
    };
    set();
    for (const method of ["prompt", "continue"]) {
        const run = agent[method].bind(agent);
        agent[method] = (...args) => {
            set();
            return run(...args);
        };
    }
}

export async function createChatAgent({
    // The console hears from the harness through this hook: status rows it
    // adds at run end (harness_note) and a turn it asks the console to run
    // (harness_continue), since the run's own listeners are gone by then.
    onHarnessEvent = null,
    chatId,
    // Server chat UUID (chat persistence). Threaded into the credential so the
    // proxy audit log can attribute this chat's LLM calls. Null for a chat that
    // has not minted one yet (or when persistence is off) — the credential then
    // degrades to the bare JWT.
    serverChatId = null,
    // Consulted again on every graph persist: a new chat's persistent id
    // may only be recorded after this agent exists.
    getServerChatId = null,
    // Which model this agent runs on. "primary" is the chat's own; "task" is
    // the cheaper role the runtime already resolves for background work, and a
    // modelId pins one outright. Defaults leave every existing caller on the
    // model it had.
    role = "primary",
    modelId = null,
    extraTools = [],
    extraToolsWith = null,
    toolsWhile = [],
    replaceToolsWith = null,
    systemPrompt: customSystemPrompt = null,
    systemPromptSuffix = "",
    runContext = null,
    getRunContext = null,
    // Optional caller hook run after every tool call. Return
    // `{ terminate: true }` to end the agent loop (e.g. a focused
    // surface that wants to break runaway loops). Receives the same
    // ctx the Agent passes.
    afterToolCall: extraAfterToolCall = null,
    // By default a `suggest_kernel` call ends the run — it's an inert UI
    // hint with no useful result for the model, so historically every
    // surface just stopped there. ChatConsole opts OUT (passes false):
    // it keeps the loop going so the model can write a closing summary,
    // and relocates the Save card to the end at agent_end
    // (finalizeRunTranscript) with a render gate to hide it until then.
    // Surfaces without that relocation machinery (KernelChat) keep the
    // terminate, otherwise a stray suggest_kernel call renders an
    // un-relocated card inline mid-transcript.
    terminateOnSuggestKernel = true,
    // Built-in tool groups this surface leaves out, with the prompt sections
    // that teach them (see TOOL_GROUPS). Unlike replaceToolsWith, the rest of
    // the chat stays: analytics, Python, sub-agents and harness memory.
    omitToolGroups = [],
} = {}) {
    const omitted = toolGroupSet(omitToolGroups);
    const runtime = await buildAgentRuntime({ serverChatId, role, modelId });

    const schema = await loadAnalyticsSchema();
    const baseSystemPrompt = buildSystemPrompt(schema, omitted);
    // Harness memory rides only on the full analytics chat. Focused
    // surfaces bring their own prompt and tool set and would neither
    // benefit from nor correctly feed the lessons.
    const usesHarnessMemory = !customSystemPrompt && !Array.isArray(replaceToolsWith);
    const memoryLedger = createRunLedger();
    let memorySection = "";
    if (usesHarnessMemory) {
        try {
            if (schema) harnessMemory.reconcileSchema(schema);
            memorySection = harnessMemory.promptSection();
            memoryLedger.setInjected(harnessMemory.promptEntries().map((e) => e.id));
        } catch (err) {
            console.warn("[chat] harness memory unavailable:", err);
        }
    }
    // The investigation graph: this chat's typed anchors and claims,
    // reloaded when a chat is reopened so an investigation resumes
    // where it stopped. Focused surfaces do not carry one.
    const attachedGraph = usesHarnessMemory ? attachLiveGraph({ chatId, serverChatId, getServerChatId, store: createGraphStore() }) : null;
    const graph = attachedGraph?.graph || null;
    const persistGraph = () => attachedGraph?.persistGraph();
    const releaseGraph = () => attachedGraph?.releaseGraph();
    // Recipes: named search-then-expand plans the harness runs end to end,
    // distilled from runs that ended in supported claims. The
    // run trace records what the model itself called this run so the
    // distiller can read the shape of a success.
    const recipeStore = usesHarnessMemory ? createRecipeStore() : null;
    const runTrace = [];
    // Recipes that ran this turn, judged at run end against what the whole
    // run cost.
    const recipeRuns = [];
    const graphSection = graph ? renderGraph(graph, { budget: GRAPH_PROMPT_BUDGET }) : "";
    if (recipeStore && schema) {
        try { recipeStore.reconcileSchema(schema); } catch (err) { console.warn("[chat] recipe store reconcile failed:", err); }
    }
    const recipesSection = recipeStore ? recipesPromptSection(recipeStore.list()) : "";
    const systemPrompt = customSystemPrompt
        ? customSystemPrompt
        : [baseSystemPrompt, memorySection, graphSection, recipesSection, systemPromptSuffix].filter(Boolean).join("\n\n");

    // Fold in tools from any MCP servers the user has connected for
    // this tenant. Each connected server contributes a prefixed set
    // of AgentTools — see `src/lib/mcp/`. A single broken server is
    // skipped rather than blocking the whole chat, and reported on
    // the agent (`_mcpFailures`) so the console can tell the user
    // which connector's tools are missing.
    let mcpTools = [];
    let mcpFailures = [];
    if (!omitted.has("mcp")) {
        try {
            const { collectMcpAgentToolsReport } = await import("./mcp/index.js");
            const report = await collectMcpAgentToolsReport(runtime.creds.tenantId);
            mcpTools = report.tools;
            mcpFailures = report.failures;
        } catch (err) {
            console.warn("[chat] MCP tool collection failed:", err);
            mcpFailures = [{ serverId: null, name: "MCP", message: err?.message || String(err) }];
        }
    }

    // Shared per-chat namespace key — every tool that collaborates
    // via the per-chat module stores (pyodide namespace,
    // toolResultStore, renderTracker) reads through this closure so
    // they all land on the same key.
    const getNamespaceKey = () => chatId || "default";

    // Forward-declare the Agent and tool registry so the rehydrator
    // (Phase 3) can close over them. Both get values further down,
    // but rehydrate is referenced by factories that build BEFORE the
    // Agent / registry exist. JS hoists these `let` bindings — the
    // closures read them at execute-time, by which point they're
    // assigned. createRehydrator itself doesn't dereference them at
    // construction; it just stores the thunks.
    let agent;
    let registry;
    const rehydrate = createRehydrator({
        getAgent: () => agent,
        getNamespaceKey,
        getToolRegistry: () => registry,
    });

    // Model-relative result caps. Live closures over the hoisted
    // `agent` binding (same pattern as getThinkingLevel) so a mid-chat
    // model switch retunes both caps on the next tool call; before the
    // Agent exists they fall back to the runtime's initial model.
    const getModelCeilingBytes = () =>
        contextCeilingBytes(agent?.state?.model?.contextWindow ?? runtime.model?.contextWindow);
    const getInlineByteCap = () =>
        Math.floor(getModelCeilingBytes() * INLINE_RESULT_WINDOW_FRACTION);

    // Phase 3: pythonTool, expand_preview, and the spawn-tool
    // factories all receive `rehydrate` and use it to recover from
    // evicted store entries on demand. analytics_query itself is the
    // SUBJECT of rehydration (it produces rows that get replayed) —
    // it doesn't need rehydrate for its own execution.
    const pythonTool = createPythonTool({
        getNamespaceKey,
        rehydrate,
        runContext,
        getRunContext,
    });
    // Every analytics_query call (the chat's own and its sub-agents', which
    // share this tool by reference) lands in the memory ledger; failures
    // that match a stored lesson carry that lesson back to the model.
    // Focused surfaces get the bare tool and no `remember`. Every surface
    // gets the schema's guidance on an invented key or enum value.
    const bareAnalyticsQueryTool = withQueryGuidance(createAnalyticsQueryTool({
        getNamespaceKey,
        getInlineByteCap,
        onResult: graph
            ? ({ toolCallId, spec, objects }) => {
                const applied = feedGraph(graph, { toolCallId, spec, objects });
                persistGraph();
                return { note: deltaNote(applied, graph, schema), graph: graphSnapshot(graph, { newKeys: applied.newKeys || [] }) };
            }
            : null,
    }), { getSchema: () => schema });
    // Memory hints go on first, then the graph's: a failing hand-written
    // query ends with the rule it broke and the pivot that applies it.
    const analyticsQueryTool = usesHarnessMemory
        ? withGraphHints(withHarnessMemory(bareAnalyticsQueryTool, { ledger: memoryLedger, store: harnessMemory, chatId: chatId || null }), { graph, getSchema: () => schema })
        : bareAnalyticsQueryTool;
    const getRows = (id) => getStoredResult(getNamespaceKey(), id)?.rows || null;
    // The reader leaf is a task-model sub-agent with no tools and a fixed
    // output schema; the dynamic import keeps piChat <-> subAgent from
    // forming a static cycle.
    const expandTools = usesHarnessMemory
        ? createExpandTools({
            graph,
            getSchema: () => schema,
            analyticsTool: analyticsQueryTool,
            persist: persistGraph,
            getRows,
            runLeaf: async ({ task, systemPrompt, outputSchema, signal, onChildReady, onEvent }) => {
                const { runSubAgent } = await import("./subAgent.js");
                return runSubAgent({ role: "task", task, systemPrompt, tools: [], outputSchema, signal, serverChatId, onChildReady, onEvent });
            },
            // Declared further down; only read while an expand runs.
            getChildRegistry: () => childRegistry,
        })
        : [];
    // Plans the harness has proposed to the model in this chat, by id, until
    // the model keeps one (save_recipe) or the proposal turn ends without it.
    const pendingRecipes = new Map();
    const harnessTurn = { active: false, proposed: null };
    const memoryTools = usesHarnessMemory
        ? [
            createRememberTool({ store: harnessMemory, ledger: memoryLedger, chatId: chatId || null }),
            ...createGraphTools({ graph, persist: persistGraph, getSchema: () => schema }),
            createPivotTool({ graph, getSchema: () => schema, analyticsTool: analyticsQueryTool }),
            ...expandTools,
            ...createRecipeTools({ graph, getSchema: () => schema, analyticsTool: analyticsQueryTool, expandRun: expandTools[0].run, store: recipeStore, persist: persistGraph, getRows, pending: pendingRecipes, onRecipeRun: (run) => recipeRuns.push(run) }),
        ]
        : [];
    const expandPreviewTool = createExpandPreviewTool({ getNamespaceKey, rehydrate, getInlineByteCap });
    // render_block stamps the renderTracker on success;
    // suggest_kernel reads the same tracker to enforce its
    // precondition. Both must share the namespace key.
    const renderBlockTool = createRenderBlockTool({ getNamespaceKey });
    const suggestKernelTool = createSuggestKernelTool({ getNamespaceKey });
    // Canvas tools — bound to the chat id so reads/writes hit the
    // right entry in the module-level canvas store. Synchronous: the
    // tools can return the freshly assigned id and a follow-up
    // canvas_inspect sees the write immediately (React state would be
    // too late since setState is batched).
    const canvasTools = createCanvasTools({ chatId: chatId || "default" });
    // Page-navigation tools — open a data-graph scope in the main
    // Explore column or a saved Studio item as a tab. Stateless: they
    // publish an intent on navigationStore that App applies. Only the
    // main analytics chat gets these (they are a built-in group; focused
    // surfaces like the kernel editor use replaceToolsWith, and a surface
    // can leave the group out with omitToolGroups).
    const navigationTools = createNavigationTools();
    const setStarterCardsTool = createSetStarterCardsTool(runtime.creds.tenantId);

    // Per-chat registry of still-running sub-agents that survived
    // past their spawn tool's return — populated by spawn_subagent /
    // spawn_batch when a user steer arrives mid-await, read by the
    // supervisor tools. Tied to this Agent instance via the closures
    // below; a new chat (or chat-reopen) gets its own registry.
    const childRegistry = createChildHandleRegistry();

    // Tool registry held in a mutable array so the spawn tools can
    // resolve names dynamically. Built in two passes: first the
    // concrete tools, then the spawn tools (which need to read from
    // the registry that already contains the rest). The `registry`
    // binding is hoisted above so the rehydrator closure can read it.
    const builtIn = {
        analytics: [
            usesHarnessMemory ? withTrace(analyticsQueryTool, runTrace) : analyticsQueryTool,
            expandPreviewTool,
            analyticsSchemaTool,
        ],
        memory: memoryTools.map((tool) => withTrace(tool, runTrace)),
        audit: AUDIT_TOOLS,
        display: [renderBlockTool, suggestKernelTool, setStarterCardsTool, setTailoredCanvasesTool, ...canvasTools],
        navigation: navigationTools,
        schema: schemaTools,
        python: [pythonTool],
        mcp: mcpTools,
        // The server refuses everyone else; offering the tools would only
        // invite a refusal.
        classifiers: runtime.creds.isAdmin ? createMessageClassifierTools() : [],
    };
    registry = [];
    // A sub-agent picks from the tools its parent's run carries, a surface's
    // own (toolsWhile) among them.
    const toolRegistry = () => agent?.state.tools ?? registry;

    // The Agent (and therefore its `steer` method) doesn't exist yet —
    // we need the agent reference to instrument it, but the spawn
    // tools need the steer-subscribe thunk at factory time. Resolve
    // this with a level of indirection: create a holder closure that
    // returns the live subscriber once the Agent is built.
    let parentSteerSubscriber = null;
    const subscribeToParentSteer = (cb) => parentSteerSubscriber?.subscribe(cb) ?? (() => {});

    // Live read of the parent chat's current thinking depth. The `agent`
    // binding is hoisted (assigned below) — same indirection the
    // rehydrator's getAgent() uses — so the closure resolves the level
    // at spawn time, reflecting any mid-chat changes the user made.
    const getThinkingLevel = () => agent?.state?.thinkingLevel;
    const spawnSub = createSpawnSubAgentTool({ toolRegistry, getNamespaceKey, subscribeToParentSteer, childRegistry, rehydrate, getThinkingLevel, serverChatId, getGraph: () => graph });
    const spawnBatch = createSpawnBatchTool({ toolRegistry, getNamespaceKey, subscribeToParentSteer, childRegistry, rehydrate, getThinkingLevel, serverChatId, getGraph: () => graph });
    // spawn_query is a narrow-purpose specialist: parent describes
    // a query it can't get right, specialist returns a validated
    // spec, parent runs analytics_query itself. Keeps the rows in
    // the parent's own context + toolResultStore — no cross-agent
    // result plumbing. spawn_query intentionally doesn't get
    // interruption semantics; it's a short, narrow call.
    const spawnQuery = createSpawnQueryTool({ toolRegistry, getThinkingLevel, serverChatId });
    // Supervisor toolkit: in the registry unless the surface leaves out
    // the supervision group. The tools return clean "no live handle"
    // errors when called with an unknown id, so they're cheap (~3 tool
    // descriptions in system prompt) but never fire unless a spawn was
    // actually interrupted.
    const supervisorTools = createSupervisorTools({ childRegistry, subscribeToParentSteer });
    if (Array.isArray(replaceToolsWith)) {
        // Focused-agent mode — skip the built-in chat family
        // entirely. MCP tools still get included (the user's
        // tenant-level connectors are a separate kind of
        // capability, not part of the analytics chat default).
        registry.push(...replaceToolsWith, ...mcpTools);
    } else {
        registry.push(...assembleChatTools({ ...builtIn, spawn: [spawnSub, spawnBatch, spawnQuery], supervision: supervisorTools }, omitted));
        // Caller-supplied extras pushed after the built-in family so
        // they take precedence for any name collision.
        if (Array.isArray(extraTools) && extraTools.length) {
            registry.push(...extraTools);
        }
        if (typeof extraToolsWith === "function") {
            const wired = extraToolsWith({ subscribeToParentSteer, childRegistry, getAgent: () => agent });
            if (Array.isArray(wired) && wired.length) registry.push(...wired);
        }
    }

    // Assign the hoisted `agent` binding so the rehydrator's
    // getAgent() closure can resolve it.
    agent = new Agent({
        initialState: {
            model: runtime.model,
            tools: registry,
            systemPrompt,
        },
        convertToLlm: runtime.convertToLlm,
        transformContext: makeTransformContext({ getNamespaceKey, getAgent: () => agent }),
        getApiKey: runtime.getApiKey,
        // Runs the caller's hook first, then the built-in suggest_kernel
        // terminator (gated per surface), then the oversized-result
        // clamp tuned to the current model's context ceiling. See
        // makeAfterToolCall — pure and unit-tested so both the
        // ChatConsole opt-out and the terminating default are covered
        // without constructing an Agent here.
        afterToolCall: makeAfterToolCall({
            extraAfterToolCall,
            terminateOnSuggestKernel,
            maxContentBytes: () => Math.ceil(getModelCeilingBytes() * CONTENT_CLAMP_WINDOW_FRACTION),
        }),
    });

    // Wrap agent.steer so spawn_subagent / spawn_batch can detect a
    // user steer mid-await without polling. Original steer behavior
    // is preserved — the message still lands in the loop's steer
    // queue. instrumentSteer fans out to subscribers synchronously.
    parentSteerSubscriber = instrumentSteer(agent);

    // Today's date rides in the analytics prompt, re-stamped as each run
    // starts. A custom prompt is used as given.
    if (!customSystemPrompt) stampCurrentDate(agent);
    if (!Array.isArray(replaceToolsWith) && toolsWhile.length) offerToolsWhile(agent, registry, toolsWhile);

    // End of every run: pair this run's failed queries with the fixes
    // that followed, store what generalises, and refresh the lessons
    // block so the next turn in this same chat already sees it. Runs
    // detached; a failure here is logged and never touches the chat.
    if (usesHarnessMemory) {
        agent.subscribe((event) => {
            if (event.type !== "agent_end") return;
            void (async () => {
                try {
                    const { runSubAgent } = await import("./subAgent.js");
                    const summary = await distillRun({
                        ledger: memoryLedger,
                        store: harnessMemory,
                        chatId: chatId || null,
                        runSubAgent: (opts) => runSubAgent({ ...opts, serverChatId }),
                    });
                    const changed = summary.stored.length || summary.pitfalls.length || summary.confirmed.length || summary.contradicted.length;
                    if (changed) {
                        setSystemPrompt(agent, replaceLessonsBlock(agent.state.systemPrompt, harnessMemory.promptSection()));
                        memoryLedger.setInjected(harnessMemory.promptEntries().map((e) => e.id));
                    }
                    // The graph block in the prompt is refreshed once per run, not
                    // per tool call, so the cached prompt prefix survives a run;
                    // within a run the model reads live state with graph_state.
                    if (graph) {
                        persistGraph();
                        setSystemPrompt(agent, replaceGraphBlock(agent.state.systemPrompt, renderGraph(graph, { budget: GRAPH_PROMPT_BUDGET })));
                    }
                    // A run whose own queries led to an expand and a supported
                    // claim becomes a recipe: the plan is distilled from the
                    // trace, replayed on its example to check it re-materializes
                    // the anchors, named by the task model, then stored. Either
                    // way the block is refreshed and the trace starts over.
                    // Everything recipes do at run end (judging the ones that
                    // ran by their claims and by the run's cost against the
                    // origin, distilling this run, replaying, describing,
                    // storing, describing again after a merge) is
                    // settleRecipeRun in graphRecipes.js, covered by its
                    // "settles a run" test; this block only hands it the
                    // chat's tools.
                    if (graph && recipeStore) {
                        // `lock` true holds the composer while the harness works or
                        // waits on the model; false releases it. `remove` drops a row.
                        const note = (id, text, detail = null, extra = {}) => { if (typeof onHarnessEvent === "function") { try { onHarnessEvent({ type: "harness_note", id, text, detail, ...extra }); } catch { /* the host's failure is its own */ } } };
                        if (harnessTurn.active) {
                            // The proposal turn just ended: the model either
                            // called save_recipe (the proposal is gone from
                            // pending) or declined in text.
                            harnessTurn.active = false;
                            const proposed = harnessTurn.proposed; harnessTurn.proposed = null;
                            const outcome = proposalOutcome({ proposed, pending: pendingRecipes, store: recipeStore, lastText: lastAssistantText(agent.state.messages) });
                            if (outcome) {
                                if (outcome.reason) { try { recipeStore.noteSettle?.({ learned: null, reason: outcome.reason }); } catch { /* optional */ } }
                                // The outcome goes after the turn that produced it; the
                                // waiting row goes away and the composer is released.
                                note("recipe-settle", "", null, { remove: true });
                                note("recipe-outcome", outcome.note, outcome.detail, { lock: false });
                            }
                        } else {
                            // This turn's trace and the recipes that ran in it are
                            // taken off the live lists before anything awaits, so
                            // the next turn starts clean even if settling is slow.
                            const settle = {
                                trace: runTrace.splice(0, runTrace.length),
                                ran: recipeRuns.splice(0, recipeRuns.length),
                                question: lastUserText(agent.state.messages),
                                calls: countRunToolCalls(agent.state.messages),
                                graph,
                                getRows,
                                store: recipeStore,
                                analyticsTool: analyticsQueryTool,
                                schema,
                                describe: (candidate) => describeRecipeWithModel({ candidate, runSubAgent: (opts) => runSubAgent({ ...opts, serverChatId }) }),
                                onPhase: (phase, info) => { if (phase === "replaying") note("recipe-settle", "Recipe: replaying the plan on this question\u2026", info?.plan || null, { lock: true }); },
                                // The decision is the model's: the plan is held as
                                // pending and put in front of the model in a turn
                                // the console runs; save_recipe keeps it.
                                propose: async ({ recipe, example, expected, replay, existing }) => {
                                    pendingRecipes.set(recipe.id, { recipe, example, expected });
                                    harnessTurn.proposed = { id: recipe.id, name: recipe.name };
                                    note("recipe-settle", "Recipe: a plan replayed; asking the model whether to keep it\u2026", describeRecipe(recipe), { lock: true });
                                    if (typeof onHarnessEvent === "function") {
                                        harnessTurn.active = true;
                                        const onSkipped = (why) => dropProposal({ recipe, why, pending: pendingRecipes, harnessTurn, store: recipeStore, note });
                                        onHarnessEvent({ type: "harness_continue", reason: "recipe proposal", onSkipped, messages: [{ role: "notification", text: recipeProposalText({ recipe, example, replay, existing }), timestamp: Date.now() }] });
                                    } else {
                                        pendingRecipes.delete(recipe.id);
                                        try { recipeStore.noteSettle?.({ learned: null, reason: "a plan was proposed but this host cannot run the model's decision" }); } catch { /* optional */ }
                                    }
                                },
                                log: (...args) => console.warn("[chat]", ...args),
                            };
                            try {
                                const settled = await settleRecipeRun(settle);
                                // A run that proposes nothing leaves no row: the reason is
                                // on the /recipes note. A replay that began and was refused
                                // takes its row down and releases the composer.
                                if (!settled?.proposed) note("recipe-settle", "", null, { remove: true, lock: false });
                            } catch (err) {
                                console.warn("[chat] recipe settle failed:", err);
                                note("recipe-settle", "Recipe: settling failed", String(err?.message || err).slice(0, 200), { lock: false });
                            }
                        }
                        setSystemPrompt(agent, replaceRecipesBlock(agent.state.systemPrompt, recipesPromptSection(recipeStore.list())));
                    }
                    if (summary.error) console.warn("[chat] harness memory distill:", summary.error);
                } catch (err) {
                    console.warn("[chat] harness memory distill failed:", err);
                }
            })();
        });
    }
    // The org's recipes: read from the trace store in the background, at
    // most once an hour per browser. The prompt block takes them in when
    // the read lands and no run is under way; a run's end refreshes it
    // anyway.
    if (recipeStore?.org) {
        void syncOrgRecipes({ cache: recipeStore.org, browserId: recipeStore.browserId(), log: (...args) => console.warn("[chat]", ...args) }).then((result) => {
            if (result?.changed && !agent.state.isStreaming) setSystemPrompt(agent, replaceRecipesBlock(agent.state.systemPrompt, recipesPromptSection(recipeStore.list())));
        }).catch((err) => console.warn("[chat] org recipes not read:", err));
    }

    // Expose the child registry on the Agent instance so ChatConsole
    // can subscribe to live state changes from still-running children
    // and reflect them in the rendered chat — see syncChildHandleState
    // in ChatConsole.jsx. Children of children (grandchildren) get
    // their OWN registries attached the same way by buildChildSupervision.
    try { agent._childHandleRegistry = childRegistry; } catch { /* noop */ }
    // Connected MCP servers whose tools could not be loaded for this
    // agent build. ChatConsole shows these above the composer.
    try { agent._mcpFailures = mcpFailures; } catch { /* noop */ }

    // The console calls this when the chat closes so the registry lets
    // the graph go; before then the footer and transcript read it live.
    agent.releaseGraph = releaseGraph;
    // The chat's investigation graph and its save, for tools built outside the
    // constructor (the triage kit) that establish anchors and claims too.
    agent.graph = graph;
    agent.persistGraph = persistGraph;
    return agent;
}
